import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { NotificationDot } from "@-/dev.improve.haniverse.components.ui.notification-dot";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@-/dev.improve.haniverse.components.ui.popover";
import { ProfileAvatar } from "@-/dev.improve.haniverse.components.ui.profile-card";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useLocalAccount } from "@-/dev.improve.haniverse.lib.auth.client";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import { Pressable, ScrollView, useWindowDimensions, View } from "@-/com.npmjs.react-native";
import { useCallback, useMemo, useRef, useState, type ComponentRef } from "react";
import {
  NavAccountDrawerAction,
  NavAccountDrawerDestinations,
  NavAccountDrawerProfileSurface,
} from "#nav-account-drawer-content";
import {
  NavAccountDrawerCloseContext,
  NavAccountDrawerNotificationsContext,
  NavAccountDrawerVisibilityContext,
} from "#nav-account-drawer-context";
import { useNavAccountDrawerNotifications } from "#use-nav-account-drawer-notifications";

/** Keeps the popover's bottom edge clear of the window under the header. */
const POPOVER_WINDOW_MARGIN = 80;

/** A row the popover adds below the account panel's own tabs; it closes the popover first. */
export interface NavAccountDrawerPopoverAction {
  icon: IconName;
  label: string;
  onPress: () => void;
  testID: string;
}

/**
 * Web's account drawer: the header's top-left control, the learner's avatar beside the name of
 * the section they are on ("Settings", "Browse"), opens a popover holding the native drawer's
 * profile surface (name, username, streak, rank, wallet and cards learned today) and its tabs.
 * Both presentations render the same components, so a tab added to the drawer reaches web too.
 *
 * The avatar and the section name are one pressable, so either opens it. A lit drawer row
 * propagates its notification dot to the avatar, as on the native trigger, and the dot hides
 * while the popover is open. Like the drawer, the profile surface reads its live stats only while
 * the popover is open. Without a local account (signed out on web) the popover holds just the
 * tabs.
 */
export function NavAccountDrawerPopover({
  actions = [],
  label,
}: {
  actions?: readonly NavAccountDrawerPopoverAction[];
  /** The current section's name, beside the avatar; `null` shows the avatar alone. */
  label: string | null;
}) {
  // The account lives in browser storage, which the server cannot read: render the signed-out
  // trigger until hydration so the first client render matches the server HTML.
  const localAccount = useLocalAccount();
  const account = useHasHydrated() ? localAccount : null;
  const triggerRef = useRef<ComponentRef<typeof PopoverTrigger>>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { hasNotifications, notifications } = useNavAccountDrawerNotifications();
  const { height: windowHeight } = useWindowDimensions();
  const close = useCallback(() => {
    triggerRef.current?.close();
  }, []);
  const visibility = useMemo(() => ({ isOpen }), [isOpen]);
  const showsNotificationDot = hasNotifications && !isOpen;
  const user =
    account === null
      ? null
      : (account.user ?? { email: account.email, name: account.email || "Guest" });

  return (
    <Popover onOpenChange={setIsOpen}>
      <PopoverTrigger asChild ref={triggerRef}>
        <Pressable
          accessibilityLabel="Account menu"
          accessibilityRole="button"
          accessibilityValue={showsNotificationDot ? { text: "New" } : undefined}
          className="-ml-1.5 h-10 min-w-0 shrink flex-row items-center gap-2 rounded-full border border-border py-1 pr-2.5 pl-1 hover:bg-accent active:bg-accent"
          // Web e2e flows select the control as "Account menu", which its DOM id keeps stable
          // whichever attribute the driver reads.
          nativeID="Account menu"
          testID="nav-account-menu-trigger"
        >
          <View className="relative size-8 shrink-0">
            {user === null ? (
              <View className="size-8 items-center justify-center rounded-full bg-muted">
                <Icon className="text-muted-foreground" name="MdPersonOutline" size={18} />
              </View>
            ) : (
              <View className="size-8 overflow-hidden rounded-full">
                <ProfileAvatar user={user} />
              </View>
            )}
            {showsNotificationDot ? (
              <NotificationDot
                className="absolute top-0 right-0"
                testID="nav-account-menu-notification-dot"
              />
            ) : null}
          </View>
          {label === null ? null : (
            <Text
              className="shrink text-lg font-bold text-foreground"
              numberOfLines={1}
              testID="nav-account-menu-section"
            >
              {label}
            </Text>
          )}
          <Icon className="shrink-0 text-muted-foreground" name="LuChevronDown" size={16} />
        </Pressable>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2.25" side="bottom" sideOffset={8}>
        <NavAccountDrawerCloseContext value={close}>
          <NavAccountDrawerNotificationsContext value={notifications}>
            <NavAccountDrawerVisibilityContext value={visibility}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ maxHeight: Math.max(0, windowHeight - POPOVER_WINDOW_MARGIN) }}
                testID="nav-account-menu-content"
              >
                {account === null ? null : (
                  <>
                    <NavAccountDrawerProfileSurface account={account} />
                    <View className="my-2 h-px bg-border" />
                  </>
                )}
                <NavAccountDrawerDestinations />
                {actions.length === 0 ? null : (
                  <>
                    <View className="my-2 h-px bg-border" />
                    {actions.map((action) => (
                      <NavAccountDrawerAction
                        icon={action.icon}
                        key={action.testID}
                        label={action.label}
                        onPress={() => {
                          close();
                          action.onPress();
                        }}
                        testID={action.testID}
                      />
                    ))}
                  </>
                )}
              </ScrollView>
            </NavAccountDrawerVisibilityContext>
          </NavAccountDrawerNotificationsContext>
        </NavAccountDrawerCloseContext>
      </PopoverContent>
    </Popover>
  );
}
